import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
    <div className="text-gray-400 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

const PrivacyPage = () => (
  <div className="min-h-screen bg-gray-950 pt-24 pb-20 px-4">
    <div className="max-w-3xl mx-auto">
      <div className="mb-12">
        <div className="text-green-400 font-mono text-sm mb-3">// legal</div>
        <h1 className="text-4xl font-black text-white mb-3">Privacy Policy</h1>
        <p className="text-gray-500 text-sm">Last updated: June 19, 2026</p>
      </div>

      <Section title="1. Overview">
        <p>
          This policy explains what personal data PlarixMC collects, why, and what rights you
          have. We are the data controller for the data described here. Contact:{' '}
          <a href="mailto:info@plarixmc.com" className="text-green-400 hover:text-green-300">info@plarixmc.com</a>.
        </p>
      </Section>

      <Section title="2. What we collect">
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="text-gray-300">Account data</span> — name, email address, and a hashed password (we never store your password in plain text).</li>
          <li><span className="text-gray-300">Billing data</span> — order history, invoices, account balance and transactions. Card and PayPal details are processed directly by Stripe and PayPal; we never see or store your full card number.</li>
          <li><span className="text-gray-300">Service data</span> — your servers, their configuration, and files you upload to them.</li>
          <li><span className="text-gray-300">Support data</span> — tickets and messages you send us.</li>
          <li><span className="text-gray-300">Technical data</span> — IP addresses and request logs used for security, rate limiting, and abuse prevention.</li>
        </ul>
      </Section>

      <Section title="3. Why we process it">
        <p>
          We process this data to provide the hosting service you purchased (contract), to
          send transactional emails such as receipts, renewal reminders, and password resets
          (contract), to keep the platform secure and prevent abuse (legitimate interest), and
          to meet accounting and tax obligations (legal obligation). We do not sell your data
          and we do not use it for third-party advertising.
        </p>
      </Section>

      <Section title="4. Cookies and local storage">
        <p>
          We use browser local storage to keep you signed in (authentication tokens). We do
          not use advertising or cross-site tracking cookies.
        </p>
      </Section>

      <Section title="5. Who we share data with">
        <p>We share data only with the processors needed to run the service:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="text-gray-300">Contabo GmbH</span> (Germany) — server infrastructure where the platform and your game servers run.</li>
          <li><span className="text-gray-300">Stripe</span> and <span className="text-gray-300">PayPal</span> — payment processing.</li>
          <li><span className="text-gray-300">Zoho</span> (EU) — transactional email delivery.</li>
        </ul>
        <p>We may disclose data if required by law or to protect our rights and users.</p>
      </Section>

      <Section title="6. How long we keep it">
        <p>
          Account data is kept while your account is active. Server files are deleted when a
          server is deleted (including automatic deletion 14 days after an unpaid due date).
          Billing records are kept as long as tax law requires. Security logs are kept for a
          short rolling period.
        </p>
      </Section>

      <Section title="7. Your rights (GDPR)">
        <p>
          You can request access to, correction of, or deletion of your personal data, ask for
          a copy in a portable format, and object to or restrict certain processing. To
          exercise any of these rights, email{' '}
          <a href="mailto:info@plarixmc.com" className="text-green-400 hover:text-green-300">info@plarixmc.com</a>.
          You can also change your name, email, and password anytime in{' '}
          <Link to="/account" className="text-green-400 hover:text-green-300">account settings</Link>.
          If you believe we are processing your data unlawfully, you have the right to lodge a
          complaint with your local data protection authority.
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          All traffic to plarixmc.com is encrypted with TLS. Passwords are hashed with bcrypt.
          Access to production systems is restricted. No system is perfectly secure, but we
          work to protect your data with industry-standard measures.
        </p>
      </Section>

      <Section title="9. Changes">
        <p>
          We may update this policy from time to time. Material changes will be announced by
          email or a notice on the site.
        </p>
      </Section>
    </div>
  </div>
);

export default PrivacyPage;
